import React, { useEffect, useState } from 'react';
import { Product } from './inventory.types';
import { useAuth } from '../../auth/useAuth';
import { API_URL } from '../../config/config';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Modal,
  Typography, Button, Divider,
} from '@mui/material';

type Props = {
  modalState: boolean;
  setModalState: React.Dispatch<React.SetStateAction<boolean>>;
  product: Product;
  inventoryId: string;
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', md: 600 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

interface FormValues {
  quantity: number;
  storeId: string;
  price: number;
  productId: string;
  productVariantId: string[];
  coupon: string[];
  size: string[];
}

interface StoreForm {
  id_BusinessLocation: any;
  id: string;
  name: string;
}

const SellProductInformationModal: React.FC<Props> = ({ modalState, setModalState, product, inventoryId }) => {
  const [stores, setStores] = useState<StoreForm[]>([]);
  const { user, isAuthenticated, token } = useAuth();
  const [form, setForm] = useState<FormValues>({
    quantity: 0,
    storeId: '',
    price: 0,
    coupon: [],
    productId: '',
    productVariantId: [],
    size: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profit, setProfit] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const getStores = async () => {
      try {
        setLoading(true);
        if (!user) return null;
        const res = await fetch(`${API_URL}/store/${user.id}/`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to fetch stores or stores.');
        }
        const inv = await res.json();
        setStores(inv.data);
      } catch (err: any) {
        setError(err.message || 'Unexpected error' + loading);
      } finally {
        setLoading(false);
      }
    };

    if (!isAuthenticated || user === null) {
      return setError('User not found or not authenticated');
    } else {
      getStores();
    }

  }, [product.id, isAuthenticated, user, loading]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(
        `${API_URL}/transaction/create-transaction/${user!.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            quantity: form.quantity,
            storeId: form.storeId,
            price: form.price,
            productId: product.id,
            productVariantId: form.productVariantId.join(';'),
            coupon: form.coupon.join(';'),
            size: form.size
              .map((id) => product!.ProductVariants?.find((v) => v.id === id)?.size)
              .join(';'),
            profit: profit,
            total: total,
            tax: 0,
            inventoryId: inventoryId,
          }),
        },
      );
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      setModalState(false);
    } catch (err: any) {
      console.error('Payment failed with error: ', err);
    }
  };

  useEffect(() => {
    if (!product || !form.quantity) return;

    const price = parseFloat(product.default_price as string);
    const cost = parseFloat(product.default_cost as string);
    const qty = form.quantity;

    setTotal(Number((price * qty).toFixed(2)));
    setProfit(Number(((price - cost) * qty).toFixed(2)));

  }, [product, form.quantity]);

  return (
    <Modal
      open={modalState}
      onClose={() => setModalState(false)}
      slotProps={{ backdrop: { onClick: () => setModalState(false) } }}
    >
      <Box sx={style} onClick={e => e.stopPropagation()}>
        <Typography variant="h5" fontWeight={600}>{error}</Typography>

        {/* Product info */}
        <Typography variant="h5" fontWeight={600}>{product.name}</Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">SKU: {product.sku}</Typography>
          <Typography variant="body2" color="text.secondary">Barcode: {product.barcode}</Typography>
        </Box>

        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[
            { label: 'Price', value: `$${product.default_price}` },
            { label: 'Profit', value: `$${profit}` },
            { label: 'Total', value: `$${total}` },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ flex: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" fontWeight={600}>{value}</Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Form */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Grid container spacing={2} direction="column">
              <Grid>
                <TextField fullWidth label="Quantity" name="quantity" value={form?.quantity} onChange={handleChange} />
              </Grid>
              <Grid>
                <FormControl fullWidth>
                  <InputLabel>Store</InputLabel>
                  <Select
                    name="store"
                    value={form?.storeId}
                    onChange={e => setForm({ ...form, storeId: e.target.value })}
                    label="Store"
                  >
                    {stores.map(store => (
                      <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <Select
                  multiple
                  name="coupon"
                  value={form.coupon || []}
                  onChange={handleChange}
                >
                  <MenuItem value="DISCOUNT10">DISCOUNT10</MenuItem>
                  <MenuItem value="SUMMER">SUMMER</MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Grid container spacing={2} direction="column">
              <Grid>
                <FormControl fullWidth>
                  <InputLabel>Variant</InputLabel>
                  <Select
                    multiple
                    name="productVariantId"
                    value={form.productVariantId || []}
                    onChange={handleChange}
                    label="Variant"
                  >
                    {product.ProductVariants?.map((variant) => (
                      <MenuItem key={variant.id} value={variant.id}>
                        {variant.color} - {variant.size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <FormControl fullWidth>
                  <InputLabel>Size</InputLabel>
                  <Select
                    multiple
                    name="size"
                    value={form.size || []}
                    onChange={handleChange}
                    label="Size"
                    renderValue={(selected) =>
                      // Map IDs back to size labels for display
                      (selected as string[])
                        .map((id) => product.ProductVariants?.find((v) => v.id === id)?.size)
                        .join(', ')
                    }
                  >
                    {product.ProductVariants?.map((variant) => (
                      <MenuItem key={variant.id} value={variant.id}>
                        {variant.size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={!form?.storeId || form.quantity <= 0}
          sx={{ mt: 3 }}
        >
          Sell Product
        </Button>

      </Box>
    </Modal>
  );
};
export default SellProductInformationModal;